/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable import/no-unresolved */
import {
    useMutation,
    useQuery,
    useReactiveVar,
    useLazyQuery,
} from '@apollo/client';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import {
    getUserInfoVar, tenantIdVar, leftNavigationItemsVar,
    showCrossReportConfigVar, showDataReconMenusVar, showReportMenuVar,
    currentLeftMenuVar, moduleIdVar,
} from 'Apollo/config';
import {
    DELETE_USER, IAM_DELETE_USER,
} from 'Apollo/Mutations';
import { GET_USERS, GET_USER_INFO } from 'Apollo/Queries';
import { ModuleEnum } from 'constants';
import Users from '../components/Users';
import { columnsConfig, listItems } from './utils';

const productName = process.env.REACT_APP_PRODUCT;

const UserContainer = () => {
    const loggedInUser = useReactiveVar(getUserInfoVar);
    const history = useHistory();
    const loggedInUserTenant = useReactiveVar(tenantIdVar);
    const [tenantId, setTenantId] = useState(loggedInUserTenant);
    const [errorMsg, setErrorMsg] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [showWarningMessage, setShowWarningMessage] = useState(false);
    const [warnMsg, setWarnMsg] = useState(null);
    const [active, setActive] = useState(false);
    const [inactive, setInactive] = useState(false);
    const [filterString, setFilterString] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState('');
    const [tableData, setTableData] = useState([]);

    /* Apollo query to fetch user details */
    const { data = {} } = useQuery(GET_USER_INFO);

    /* Side effect when user details updates */
    const [getUsers, users] = useLazyQuery(GET_USERS, {
        variables: {
            input: {
                tenantId: parseInt(tenantId, 10),
                getAll: true,
            },
        },
        fetchPolicy: 'no-cache',
    });

    /* Apollo mutation to delete user */
    const [deleteUser, { loading: deleteUserLoading }] = useMutation(DELETE_USER);

    /* Apollo mutation to delete user from IAM */
    const [iamDeleteUser, { loading: deleteUserIAMLoading }] = useMutation(IAM_DELETE_USER);

    /** Calls iamDeleteUser() and  deleteUser() */
    const callDeleteUser = async user => {
        try {
            const _iamDeletRes = await iamDeleteUser({
                variables: {
                    input: {
                        email: user.email,
                        organizationId: user.iamTenant.iamOrganizationId,
                        requestingUserEmail: data && data.getUser.email,
                        product: productName,
                    },
                },
            });
            if (_iamDeletRes.data.IAMdeleteUser.status === 'SUCCESS') {
                try {
                    const deleteUserRes = await deleteUser({
                        variables: {
                            input: {
                                userId: user.userId,
                            },
                        },
                    });
                    if (deleteUserRes.data.deleteUser) {
                        setSuccessMsg('User deleted Successfully!');
                        setShowSuccessAlert(true);
                        users.refetch();
                    }
                } catch (deleteUserErr) {
                    setErrorMsg('An error occurred while deleting the user.');
                    setShowErrorAlert(true);
                }
            }
        } catch (_iamDeleteErr) {
            setErrorMsg(_iamDeleteErr.message);
            setShowErrorAlert(true);
        }
    };

    /** Shows warning modal and message and  calls callDeleteUser() */
    const deleteUserEvent = async user => {
        if (user.userId === loggedInUser.getUser.userId) {
            setShowWarningMessage(true);
            setWarnMsg('Requesting user is not allowed to perform operations on self.');
        } else {
            callDeleteUser(user);
        }
    };

    const columns = columnsConfig(history, deleteUserEvent);

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            showCrossReportConfigVar(false);
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">User Management</span>,
        },
    ];

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        showCrossReportConfigVar(false);
        showDataReconMenusVar(false);
        showReportMenuVar(false);
        currentLeftMenuVar(ModuleEnum.USER);
    }, []);
    /* Side effect when loggedIn user detail is available */
    useEffect(() => {
        if (data?.getUser?.iamTenant) {
            setTenantId(data.getUser.iamTenant.tenantId);
        }
    }, [data]);

    /* Side effect when tenantId is available */
    useEffect(() => {
        getUsers();
    }, [tenantId]);

    /* Side effect when users details updates */
    useEffect(() => {
        if (users.data && users.data.getUsers) {
            setTableData(users.data.getUsers);
        }
    }, [users]);

    /* Side effect when filterString updates */
    useEffect(() => {
        const searchString = filterString.trim().toLowerCase();
        if (searchString.length > 0) {
            setTableData(users.data && users.data.getUsers.filter(e => e.fullName.toLowerCase().match(searchString)));
        } else {
            setTableData(users.data && users.data.getUsers);
        }
    }, [filterString]);

    /* Side effect when active or inactive status updates */
    useEffect(() => {
        // eslint-disable-next-line no-lonely-if
        if (active === false && inactive === false) {
            setTableData(users.data && users.data.getUsers);
        } else if (active === true && inactive === false) {
            setTableData(users.data && users.data.getUsers && users.data.getUsers.filter(e => e.iamUserStatus === 'ENABLED'));
        } else if (active === false && inactive === true) {
            setTableData(users.data && users.data.getUsers && users.data.getUsers.filter(e => e.iamUserStatus === 'DISABLED'));
        } else {
            setTableData(users.data && users.data.getUsers);
        }
    }, [active, inactive]);

    return (
        <Users
            history={history}
            breadcrumbItems={breadcrumbItems}
            columns={columns}
            tableData={tableData}
            errorMsg={errorMsg}
            showErrorAlert={showErrorAlert}
            setShowErrorAlert={setShowErrorAlert}
            showWarningMessage={showWarningMessage}
            warnMsg={warnMsg}
            setShowWarningMessage={setShowWarningMessage}
            active={active}
            setActive={setActive}
            inactive={inactive}
            setInactive={setInactive}
            filterString={filterString}
            setFilterString={setFilterString}
            successMsg={successMsg}
            showSuccessAlert={showSuccessAlert}
            setShowSuccessAlert={setShowSuccessAlert}
            usersLoading={users.loading}
            deleteUserIAMLoading={deleteUserIAMLoading}
            deleteUserLoading={deleteUserLoading}
        />
    );
};

export default UserContainer;
